// =====================================================
// utils/exportarTorneoPdf.ts
// Módulo de exportación de torneo a PDF usando jsPDF 1.5.x
// =====================================================

import jsPDF from 'jspdf';
import { ZonaCruces, Ronda, Equipo } from './torneos';

// ---------------------------------------------------------
// Constantes de layout — sección zonas (portrait A4)
// ---------------------------------------------------------
const MARGEN_IZQ = 15;
const MARGEN_SUP = 15;
const Y_MAX = 282;           // 297 - 15 margen inferior
const ALTO_TITULO_ZONA = 8;
// FIX 4: columna de resultado un poco más ancha (era 50mm, ahora 60mm)
const ANCHO_RESULTADO = 60;  // mm de ancho para la columna de resultado
const ALTO_PARTIDO = 8;      // un poco más de espacio por fila
const ALTO_SEPARADOR = 4;

// ---------------------------------------------------------
// Helpers
// ---------------------------------------------------------

export function formatearNombreArchivo(nombre: string): string {
  const base = nombre
    ? nombre.toLowerCase().replace(/\s+/g, '-')
    : 'torneo';
  return `torneo-${base}.pdf`;
}

export function resolverNombreParticipante(slot: string | Equipo | null | undefined): string {
  if (slot === null) return 'BYE';
  if (slot === undefined) return '-';
  if (typeof slot === 'string') {
    return slot.trim() === '' ? '-' : slot;
  }
  return slot.nombre;
}

// ---------------------------------------------------------
// Sección de zonas (portrait)
// FIX 1: línea de resultado alineada con la base del texto
// FIX 2: encabezado con género, categoría y fecha si están disponibles
// ---------------------------------------------------------

interface InfoTorneo {
  genero?: string;
  categoria?: string;
}

export function generarSeccionZonas(doc: any, zonas: ZonaCruces[], info?: InfoTorneo): void {
  if (!zonas || zonas.length === 0) return;

  let cursorY = MARGEN_SUP + 20; // Debajo del encabezado principal

  for (const zona of zonas) {
    const numPartidos = zona.partidos ? zona.partidos.length : 0;
    // +5mm para el encabezado "Resultado"
    const alturaZona = ALTO_TITULO_ZONA + 5 + numPartidos * ALTO_PARTIDO + ALTO_SEPARADOR;

    if (cursorY + alturaZona > Y_MAX) {
      doc.addPage();
      cursorY = MARGEN_SUP;
    }

    // Nombre de zona
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(zona.nombre || 'Zona', MARGEN_IZQ, cursorY);
    cursorY += ALTO_TITULO_ZONA;

    // Encabezado de columnas
    const xResultadoCol = MARGEN_IZQ + 90;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text('Resultado', xResultadoCol, cursorY);
    doc.setTextColor(0, 0, 0);
    cursorY += 5;

    // Partidos
    if (zona.partidos) {
      for (const partido of zona.partidos) {
        const local = resolverNombreParticipante(partido.local);
        const visitante = resolverNombreParticipante(partido.visitante);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(`${local} vs ${visitante}`, MARGEN_IZQ, cursorY);

        // FIX 1: la línea queda en la base del texto (cursorY en lugar de cursorY - 1)
        // En jsPDF el texto se dibuja con la baseline en Y, así que la línea en cursorY
        // queda exactamente bajo el texto.
        doc.line(xResultadoCol, cursorY, xResultadoCol + ANCHO_RESULTADO, cursorY);

        cursorY += ALTO_PARTIDO;
      }
    }

    cursorY += ALTO_SEPARADOR;
  }
}

// ---------------------------------------------------------
// Helpers del bracket
// ---------------------------------------------------------

/**
 * Resuelve el nombre para un slot del bracket.
 * Si el slot está vacío pero hay dePartido, muestra "Gan. Px".
 */
function resolverNombreBracket(slot: any, partido: any, esLocal: boolean): string {
  if (slot && typeof slot === 'object' && slot.nombre) {
    const n = slot.nombre as string;
    return n.length > 24 ? n.substring(0, 24) : n;
  }
  if (slot && typeof slot === 'string' && slot.trim() !== '') {
    const n = slot.trim();
    return n.length > 24 ? n.substring(0, 24) : n;
  }
  if (partido.dePartido && Array.isArray(partido.dePartido)) {
    const idOrigen = esLocal ? partido.dePartido[0] : partido.dePartido[1];
    if (idOrigen != null) return `Gan. P${idOrigen}`;
  }
  return '-';
}

/**
 * FIX 3: Devuelve el nombre de la fase según la cantidad de partidos en la ronda.
 */
function nombreFase(numPartidos: number): string {
  switch (numPartidos) {
    case 1:  return 'Final';
    case 2:  return 'Semifinal';
    case 4:  return 'Cuartos de Final';
    case 8:  return 'Octavos de Final';
    case 16: return 'Dieciseisavos';
    default: return `Ronda de ${numPartidos * 2}`;
  }
}

// ---------------------------------------------------------
// Sección del cuadro final
// FIX 2: crear el doc landscape desde el inicio (no addPage)
//        y controlar si hay zonas para decidir si es un PDF separado
// FIX 3: mostrar nombre de fase sobre cada columna
// FIX 4: columna de resultado más ancha dentro del rect
// ---------------------------------------------------------

/**
 * Genera el PDF del cuadro de eliminación en orientación landscape.
 * Se llama con un doc ya creado en landscape.
 */
export function generarSeccionCuadroFinal(doc: any, rondas: Ronda[]): void {
  if (!rondas || rondas.length === 0) return;

  // El doc ya viene creado en landscape — no agregar página

  // Dimensiones landscape A4
  const PW = 297;
  const PH = 210;
  const ML = 10;
  const MT = 10;
  const MR = 10;
  const MB = 10;

  const ANCHO_UTIL = PW - ML - MR;  // 277 mm
  const ALTO_UTIL  = PH - MT - MB;  // 190 mm

  const TITULO_H   = 12; // espacio para título principal
  const FASE_H     = 7;  // espacio para el nombre de fase sobre cada columna
  const AREA_H     = ALTO_UTIL - TITULO_H - FASE_H; // área disponible para los partidos

  const numRondas      = rondas.length;
  const numPartidosR1  = rondas[0].partidos.length;

  // Alto de cada rect de partido
  const GAP_ENTRE_PARTIDOS = 3;
  const altoDisponible = AREA_H - (numPartidosR1 - 1) * GAP_ENTRE_PARTIDOS;
  const ALTO_RECT = Math.max(16, Math.min(28, altoDisponible / numPartidosR1));

  // Ancho de cada columna
  const GAP_ENTRE_RONDAS = 5;
  const ANCHO_RECT = (ANCHO_UTIL - (numRondas - 1) * GAP_ENTRE_RONDAS) / numRondas;

  // FIX 4: columna de resultado más ancha dentro del rect (18mm en lugar de 12mm)
  const ANCHO_COL_RES = 18;

  // Título principal
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Cuadro de Eliminación', ML, MT + 8);

  // Precalcular posiciones de cada partido
  const posiciones: Array<Array<{ x: number; y: number; h: number }>> = [];

  for (let r = 0; r < numRondas; r++) {
    const nPartidos = rondas[r].partidos.length;
    const x = ML + r * (ANCHO_RECT + GAP_ENTRE_RONDAS);
    const espacioRonda = nPartidos * ALTO_RECT + (nPartidos - 1) * GAP_ENTRE_PARTIDOS;
    const yInicio = MT + TITULO_H + FASE_H + (AREA_H - espacioRonda) / 2;

    posiciones[r] = [];
    for (let p = 0; p < nPartidos; p++) {
      posiciones[r][p] = {
        x,
        y: yInicio + p * (ALTO_RECT + GAP_ENTRE_PARTIDOS),
        h: ALTO_RECT,
      };
    }
  }

  // Dibujar nombre de fase sobre cada columna (FIX 3)
  for (let r = 0; r < numRondas; r++) {
    const nPartidos = rondas[r].partidos.length;
    const fase = nombreFase(nPartidos);
    const x = ML + r * (ANCHO_RECT + GAP_ENTRE_RONDAS);
    const yFase = MT + TITULO_H + FASE_H - 1;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text(fase, x, yFase);
  }

  // Dibujar partidos y conectores
  doc.setTextColor(30, 30, 30);

  for (let r = 0; r < numRondas; r++) {
    const ronda = rondas[r];

    for (let p = 0; p < ronda.partidos.length; p++) {
      const partido = ronda.partidos[p];
      const { x, y, h } = posiciones[r][p];

      const nombreLocal     = resolverNombreBracket(partido.local, partido, true);
      const nombreVisitante = resolverNombreBracket(partido.visitante, partido, false);

      // Rectángulo exterior del partido
      doc.setDrawColor(80, 80, 80);
      doc.rect(x, y, ANCHO_RECT, h);

      // Línea horizontal divisoria (separa local de visitante)
      const yMitad = y + h / 2;
      doc.setDrawColor(160, 160, 160);
      doc.line(x, yMitad, x + ANCHO_RECT, yMitad);

      // Línea vertical columna resultado (FIX 4: más ancha)
      const xRes = x + ANCHO_RECT - ANCHO_COL_RES;
      doc.setDrawColor(160, 160, 160);
      doc.line(xRes, y, xRes, y + h);

      // Textos de nombres
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(20, 20, 20);
      doc.text(nombreLocal,     x + 2, y + h / 4 + 1.5);
      doc.text(nombreVisitante, x + 2, y + (3 * h) / 4 + 1.5);

      // Etiqueta "Res." en la columna de resultado
      doc.setFontSize(5);
      doc.setTextColor(120, 120, 120);
      doc.text('Res.', xRes + 1, y + 4);

      // Número de partido
      doc.setFontSize(5);
      doc.setTextColor(100, 100, 100);
      doc.text(`P${partido.id}`, x + ANCHO_RECT - ANCHO_COL_RES - 8, y + 3.5);

      // Líneas conectoras hacia la siguiente ronda
      if (r < numRondas - 1) {
        const xDer = x + ANCHO_RECT;
        const yCentro = y + h / 2;
        const pDestino = Math.floor(p / 2);
        const dest = posiciones[r + 1][pDestino];
        const yCentroDest = dest.y + dest.h / 2;
        const xConector = xDer + GAP_ENTRE_RONDAS / 2;

        doc.setDrawColor(120, 120, 120);
        doc.line(xDer,      yCentro,     xConector, yCentro);
        doc.line(xConector, yCentro,     xConector, yCentroDest);
        doc.line(xConector, yCentroDest, dest.x,    yCentroDest);
      }
    }
  }

  // Restaurar
  doc.setDrawColor(0, 0, 0);
  doc.setTextColor(0, 0, 0);
}

// ---------------------------------------------------------
// Función principal exportarTorneoPdf
// FIX 2: el cuadro final siempre va en un doc landscape separado.
//        Si hay zonas, se genera primero el PDF de zonas (portrait)
//        y luego el de cuadro final (landscape), ambos en nueva pestaña.
// ---------------------------------------------------------
export function exportarTorneoPdf(torneo: any, nombreTorneo: string, info?: InfoTorneo): void {
  if (!torneo) return;

  const tieneZonas  = torneo.zonas  && Array.isArray(torneo.zonas)  && torneo.zonas.length  > 0;
  const tieneRondas = torneo.rondas && Array.isArray(torneo.rondas) && torneo.rondas.length > 0;

  // Línea de subtítulo con género y categoría
  const partes: string[] = [];
  if (info?.genero)    partes.push(info.genero);
  if (info?.categoria) partes.push(`Cat. ${info.categoria}`);
  const subtitulo = partes.join(' · ');

  // ── PDF de zonas (portrait) ──────────────────────────────
  if (tieneZonas) {
    const docZonas = new jsPDF('p', 'mm', 'a4');

    // Encabezado
    docZonas.setFontSize(16);
    docZonas.setFont('helvetica', 'bold');
    docZonas.text(nombreTorneo || 'Torneo', 15, 18);

    if (subtitulo) {
      docZonas.setFontSize(10);
      docZonas.setFont('helvetica', 'normal');
      docZonas.text(subtitulo, 15, 25);
      docZonas.line(15, 28, 195, 28);
    } else {
      docZonas.line(15, 22, 195, 22);
    }

    generarSeccionZonas(docZonas, torneo.zonas as ZonaCruces[], info);

    const blobZonas = (docZonas as any).output('blob');
    window.open(URL.createObjectURL(blobZonas), '_blank');
  }

  // ── PDF del cuadro final (landscape) ────────────────────
  if (tieneRondas) {
    const docCuadro = new jsPDF('l', 'mm', 'a4');

    generarSeccionCuadroFinal(docCuadro, torneo.rondas as Ronda[]);

    const blobCuadro = (docCuadro as any).output('blob');
    window.open(URL.createObjectURL(blobCuadro), '_blank');
  }
}
